import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// @atlaskit
import { Grid, GridColumn } from '@atlaskit/page';
import ArrowLeftIcon from '@atlaskit/icon/glyph/arrow-left';
import Button, { ButtonGroup } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import Select from '@atlaskit/select';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import WatchFilledIcon from '@atlaskit/icon/glyph/watch-filled';

// @source
import { Header, DynamicSelection, Chart } from './components';
import {
  Messages, Fragment, QueryBuilder, ConvertQuery,
} from '../../components';
import { Table as APIs } from '../../../server';
import Layout from '../../templates/Layout';

const initialState = {
  builder: false,
  chart: '',
  isLoading: 'table',
  items: [],
  message: false,
  table: '',
};

const initialStatic = {
  columns: [],
  fields: [],
  query: '',
};

class Trend extends Component {
  constructor(props) {
    super(props);
    this.state = initialState;
    this.static = initialStatic;
    this.unmount = false;
    this.onChangeHandle = this.onChangeHandle.bind(this);
    this.onClickDrawChart = this.onClickDrawChart.bind(this);
    this.onCloseMessage = this.onCloseMessage.bind(this);
    this.onToggleColumn = this.onToggleColumn.bind(this);
    this.onToggleGroup = this.onToggleGroup.bind(this);
    this.onToggleGroupVisible = this.onToggleGroupVisible.bind(this);
    this.onToggleQueryBuilder = this.onToggleQueryBuilder.bind(this);
    this.setQueryBuilderOutput = this.setQueryBuilderOutput.bind(this);
  }

  componentDidMount() {
    if (!this.props.tables.length) {
      this.fetchTableList();
    }
  }

  componentWillUnmount() {
    this.unmount = true;
  }

  onChangeHandle(option) {
    const { value } = option;
    const result = this.state.items.find(elem => elem.title === value);

    if (!result) {
      this.fetchTableColumns(value);
    } else {
      this.setState({ table: value });
    }
  }

  onClickDrawChart(chart) {
    if (chart === 'output' && this.static.query.length < 5) {
      this.setState({
        message: (
          <SectionMessage appearance="error">
            I need to create a query
          </SectionMessage>
        ),
      });
    } else {
      this.setState({
        chart,
        message: '',
      });
    }
  }

  onToggleColumn(table, column) {
    this.setStateItemChildren(table, column, child => ({
      isSelected: !child.isSelected,
    }));
  }

  onToggleGroup(table, isSelected) {
    this.setStateItems(table, item => ({
      isOpen: false,
      children: item.children.map(child => ({
        ...child,
        isSelected,
      })),
    }));
  }

  onToggleGroupVisible(table) {
    this.setStateItems(table, child => ({
      isOpen: !child.isOpen,
    }));
  }

  onCloseMessage() {
    this.setState({
      message: false,
    });
  }

  onToggleQueryBuilder() {
    this.setState(state => ({
      builder: !state.builder,
      message: '',
    }));
  }

  setQueryBuilderFields(items) {
    const refs = {
      children: [],
      columns: [],
      fields: [],
      table: [],
    };

    items.forEach((item) => {
      const selected = item.children.filter(child => child.isSelected);
      if (selected.length) {
        const names = selected.map(elem => elem.name);
        refs.children.push(...item.children);
        refs.columns.push(`${item.title}$${names.join(':')}`);
        refs.table.push(item.title);
      }
    });

    refs.children.sort((prev, current) => (
      prev.name < current.name ? -1 : 1
    ));

    if (refs.children.length) {
      refs.children.forEach((child) => {
        const filter = refs.children.filter(item => item.name === child.name);
        if (filter.length === refs.table.length) {
          const find = refs.fields.find(field => field.name === child.name);
          if (!find) {
            refs.fields.push(child);
          }
        }
      });
    }

    this.setStatic({
      columns: refs.columns,
      fields: refs.fields,
    });
  }

  setStateItems(table, props = () => { }) {
    const items = this.state.items.map((item) => {
      if (item.title === table) {
        return { ...item, ...props(item) };
      }
      return item;
    });

    this.setState({ items });
    this.setQueryBuilderFields(items);
  }

  setStateItemChildren(table, column, props = () => { }) {
    this.setStateItems(table, item => ({
      children: item.children.map((child) => {
        if (child.name === column) {
          return { ...child, ...props(child) };
        }
        return child;
      }),
    }));
  }

  setStateUnmount(state) {
    if (this.unmount) return;
    this.setState(state);
  }

  setStatic(state) {
    this.static = { ...this.static, ...state };
  }

  setQueryBuilderOutput(template) {
    const query = ConvertQuery(template);
    this.setStatic({ query });
  }

  fetchTableList() {
    APIs.table({
      dispatch: this.props.dispatch,
      success: () => {
        this.setStateUnmount({
          isLoading: '',
        });
      },
      error: (messages) => {
        this.setStateUnmount({
          isLoading: '',
          message: (
            <Messages
              items={messages}
              onClose={this.onCloseMessage}
            />
          ),
        });
      },
    });
  }

  fetchTableColumns(table) {
    this.setState({
      isLoading: 'column',
      table,
    });

    APIs.column({
      data: { table },
      dispatch: this.props.dispatch,
      success: (columns) => {
        this.setStateUnmount({
          isLoading: '',
          items: [
            ...this.state.items, {
              title: table,
              children: columns.map(item => ({
                name: item.ColumnName,
                type: item.ColumnType,
                isSelected: false,
              })),
              isOpen: false,
            },
          ],
        });
      },
      error: (messages) => {
        this.setStateUnmount({
          isLoading: '',
          message: (
            <Messages
              items={messages}
              onClose={this.onCloseMessage}
            />
          ),
        });
      },
    });
  }

  renderTableSelect() {
    const { tables } = this.props;
    const { isLoading } = this.state;
    const options = tables.map(val => ({
      label: val.Table,
      value: val.Table,
    }));

    return (
      <Select
        isLoading={isLoading === 'table' && !tables.length}
        onChange={this.onChangeHandle}
        options={options}
        placeholder="Select a table"
      />
    );
  }

  renderFirstButtonGroup() {
    const { columns } = this.static;

    return (
      <ButtonGroup>
        <Button
          appearance="subtle"
          iconBefore={<SettingsIcon size="small" />}
          isDisabled={!columns.length}
          onClick={this.onToggleQueryBuilder}
        >
          Create query
        </Button>
        <Button
          iconBefore={<WatchFilledIcon size="small" />}
          isDisabled={!columns.length}
          onClick={() => this.onClickDrawChart('live')}
        >
          Live
        </Button>
      </ButtonGroup>
    );
  }

  renderSecondButtonGroup() {
    return (
      <ButtonGroup>
        <Button
          appearance="subtle"
          iconBefore={<ArrowLeftIcon size="small" />}
          onClick={this.onToggleQueryBuilder}
        >
          Choose
        </Button>
        <Button
          iconBefore={<WatchFilledIcon size="small" />}
          onClick={() => this.onClickDrawChart('output')}
        >
          Get Output
        </Button>
      </ButtonGroup>
    );
  }

  render() {
    const {
      builder,
      chart,
      isLoading,
      items,
      message,
      table,
    } = this.state;

    return (
      <Layout>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=0" />
        <Grid>
          <GridColumn medium={12}>
            <Header />
          </GridColumn>
          <Fragment render={!builder}>
            <GridColumn medium={12}>
              {this.renderTableSelect()}
            </GridColumn>
            <Fragment render={items.length}>
              <DynamicSelection
                isLoading={isLoading === 'column'}
                items={items}
                onToggleColumn={this.onToggleColumn}
                onToggleGroup={this.onToggleGroup}
                onToggleGroupVisible={this.onToggleGroupVisible}
                table={table}
              />
              <GridColumn medium={12}>
                {this.renderFirstButtonGroup()}
              </GridColumn>
            </Fragment>
          </Fragment>
          <Fragment render={builder}>
            <GridColumn medium={12}>
              <QueryBuilder
                fields={this.static.fields}
                output={this.setQueryBuilderOutput}
              />
              {this.renderSecondButtonGroup()}
            </GridColumn>
          </Fragment>
          <GridColumn medium={12}>
            {message}
          </GridColumn>
        </Grid>
        <Fragment render={chart}>
          <Chart
            columns={this.static.columns}
            onClose={() => this.setState({ chart: '' })}
            query={this.static.query}
            type={this.state.chart}
          />
        </Fragment>
        <br />
      </Layout>
    );
  }
}

Trend.propTypes = {
  dispatch: PropTypes.func.isRequired,
  tables: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

const mapStateToProps = state => ({
  tables: state.Table,
});

export default connect(mapStateToProps)(Trend);
